import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, api, type RoomOperationsResponse } from "../api/client.js";
import { asMoment } from "../format.js";

const REFRESH_MS = 5_000;
type Room = RoomOperationsResponse["rooms"][number];

function placementSummary(user: Room["users"][number]) {
  const placements = Object.entries(user.tablePlacements)
    .filter((entry): entry is [string, string] => Boolean(entry[1]))
    .map(([game, table]) => `${game}: ${table}`);
  return placements.length ? placements.join(" · ") : "Roaming";
}

export function Rooms() {
  const [data, setData] = useState<RoomOperationsResponse | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const request = useRef<AbortController | null>(null);

  const load = useCallback(async (polling = false) => {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    try {
      const next = await api.rooms(controller.signal, polling);
      setData(next);
      setSelectedRoomId((current) =>
        current && next.rooms.some((room) => room.roomId === current)
          ? current
          : (next.rooms[0]?.roomId ?? null),
      );
      setError(null);
    } catch (caught) {
      if (!controller.signal.aborted) {
        setError(caught instanceof ApiError ? caught.message : "Could not load rooms.");
      }
    } finally {
      if (request.current === controller) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const interval = window.setInterval(() => void load(true), REFRESH_MS);
    return () => {
      window.clearInterval(interval);
      request.current?.abort();
    };
  }, [load]);

  const selected = data?.rooms.find((room) => room.roomId === selectedRoomId) ?? null;
  const users = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    if (!selected || !normalized) return selected?.users ?? [];
    return selected.users.filter((user) =>
      [user.displayName, user.userId, user.seatId, ...Object.values(user.tablePlacements)].some(
        (value) => value?.toLocaleLowerCase().includes(normalized),
      ),
    );
  }, [query, selected]);

  if (loading && !data) return <p className="empty">Loading rooms…</p>;
  if (!data)
    return (
      <section className="body">
        <p className="banner" role="alert">
          {error}
        </p>
        <button className="btn btn-quiet" onClick={() => void load()} type="button">
          Retry
        </button>
      </section>
    );

  const connected = data.rooms.reduce((total, room) => total + room.connectedClients, 0);
  const reconnecting = data.rooms.reduce((total, room) => total + room.reconnectingClients, 0);
  const capacity = data.rooms.reduce((total, room) => total + room.maxClients, 0);
  const seated = data.rooms.reduce(
    (total, room) => total + room.users.filter((user) => user.seatId).length,
    0,
  );

  return (
    <section className="body room-operations">
      <div className="room-refresh-state">
        <span>Snapshot {asMoment(data.generatedAt)}</span>
        <span>Auto-refreshes every 5 seconds</span>
      </div>
      {error ? (
        <div className="banner" role="alert">
          Latest refresh failed. {error}
        </div>
      ) : null}
      <div className="poker-status-grid">
        <article className="card poker-status-card">
          <span className="label-13 dim">Active rooms</span>
          <strong>{data.rooms.length}</strong>
          <small>runtime rooms</small>
        </article>
        <article className="card poker-status-card">
          <span className="label-13 dim">Connected</span>
          <strong>{connected}</strong>
          <small>users across rooms</small>
        </article>
        <article className="card poker-status-card">
          <span className="label-13 dim">Reconnecting</span>
          <strong>{reconnecting}</strong>
          <small>inside the recovery window</small>
        </article>
        <article className="card poker-status-card">
          <span className="label-13 dim">Seated</span>
          <strong>{seated}</strong>
          <small>users at a table</small>
        </article>
        <article className="card poker-status-card">
          <span className="label-13 dim">Capacity</span>
          <strong>
            {connected}/{capacity}
          </strong>
          <small>connected / maximum</small>
        </article>
      </div>
      <div className="room-layout">
        <div className="card room-list">
          <table>
            <thead>
              <tr>
                <th>Room</th>
                <th>Users</th>
                <th>Seats</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.rooms.map((room) => (
                <tr
                  className={`clickable-row${selected?.roomId === room.roomId ? " selected" : ""}`}
                  key={room.roomId}
                  onClick={() => setSelectedRoomId(room.roomId)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedRoomId(room.roomId);
                    }
                  }}
                  tabIndex={0}
                >
                  <td>
                    <strong>{room.roomId}</strong>
                    <small>{room.environmentInstanceId ?? "No environment"}</small>
                  </td>
                  <td>
                    {room.connectedClients}
                    {room.reconnectingClients ? ` (+${room.reconnectingClients})` : ""}/
                    {room.maxClients}
                  </td>
                  <td>{room.users.filter((user) => user.seatId).length}</td>
                  <td>
                    <span
                      className={`environment-state environment-state-${room.acceptingJoins ? "active" : "draining"}`}
                    >
                      {room.acceptingJoins ? "Open" : "Full"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.rooms.length === 0 ? <p className="empty">No active rooms.</p> : null}
        </div>
        <aside className="card room-detail" aria-label="Room detail">
          {selected ? (
            <>
              <div className="card-bar">
                <div>
                  <strong>{selected.roomId}</strong>
                  <small>
                    {selected.sceneId} · {selected.voiceMode}
                  </small>
                </div>
                <span className="chip">
                  {selected.connectedClients}
                  {selected.reconnectingClients ? ` (+${selected.reconnectingClients})` : ""}/
                  {selected.maxClients}
                </span>
              </div>
              <label className="room-search">
                <span>Find a user</span>
                <input
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Name, user ID, seat or table"
                  type="search"
                  value={query}
                />
              </label>
              <div className="room-users">
                <table>
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Location</th>
                      <th>Presence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.sessionId}>
                        <td>
                          <strong>{user.displayName}</strong>
                          <small className="mono-12">{user.userId}</small>
                        </td>
                        <td>
                          <strong>{user.seatId ?? "Not seated"}</strong>
                          <small>{placementSummary(user)}</small>
                        </td>
                        <td>
                          {user.reconnecting ? "Reconnecting" : user.away ? "Away" : "Active"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {users.length === 0 ? <p className="empty">No users match this view.</p> : null}
              </div>
            </>
          ) : (
            <p className="empty">Select a room to inspect its users.</p>
          )}
        </aside>
      </div>
    </section>
  );
}
